import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle, RefreshCw, X } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Barra de estado de mensajes
 *
 * Muestra:
 * - Mensajes de error
 * - Mensajes de éxito
 *
 * Nota: El indicador de streaming se maneja en el botón de enviar (no aquí)
 */

export interface MessageStatusBarProps {
  error: string | null;
  successMessage: string | null;
  isStreaming: boolean;
  onCancelStreaming: () => void;
  onDismissError?: () => void;
  onDismissSuccess?: () => void;
  onRetry?: () => void;
  /** C3: Show streaming timeout indicator */
  isStreamingTimeout?: boolean;
}

const MessageStatusBar: React.FC<MessageStatusBarProps> = ({
  error,
  successMessage,
  isStreaming,
  onCancelStreaming,
  onDismissError,
  onDismissSuccess,
  onRetry,
  isStreamingTimeout = false,
}) => {
  const { theme } = useTheme();
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [lastDismissedError, setLastDismissedError] = useState<string | null>(null);

  // Track last error for persistence after auto-dismiss
  useEffect(() => {
    if (error) {
      setLastDismissedError(null); // Clear stale dismissed error when a new active error appears
    }
  }, [error]);

  // Clear persisted error when a successful operation occurs
  useEffect(() => {
    if (successMessage) {
      setLastDismissedError(null);
    }
  }, [successMessage]);

  // A1: Auto-dismiss success messages after 4 seconds
  useEffect(() => {
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    if (successMessage && onDismissSuccess) {
      successTimerRef.current = setTimeout(onDismissSuccess, 4000);
    }
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, [successMessage, onDismissSuccess]);

  // C2: Auto-dismiss error messages after 8 seconds, persist for later viewing
  useEffect(() => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    if (error && onDismissError) {
      errorTimerRef.current = setTimeout(() => {
        setLastDismissedError(error);
        onDismissError();
      }, 8000);
    }
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, [error, onDismissError]);

  // Solo mostrar errores, mensajes de éxito, timeout de streaming, o último error
  if (!error && !successMessage && !isStreamingTimeout && !lastDismissedError) {
    return null;
  }

  return (
    <div className="px-4 py-2 space-y-2">
      {/* Mensaje de error */}
      {error && (
        <div
          className={`flex items-center gap-2 text-sm ${theme.colors.text.error} ${theme.colors.bg.error} px-3 py-2 rounded`}
          role="alert"
          aria-live="assertive"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          {/* C5: Retry button */}
          {onRetry && (
            <button
              onClick={() => {
                onDismissError?.();
                onRetry();
              }}
              className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-xs hover:opacity-70 transition-opacity border border-current/20"
              aria-label="Reintentar último mensaje"
            >
              <RefreshCw className="w-3 h-3" />
              <span>Reintentar</span>
            </button>
          )}
          {onDismissError && (
            <button
              onClick={onDismissError}
              className="flex-shrink-0 hover:opacity-70 transition-opacity"
              aria-label="Cerrar mensaje de error"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* C3: Streaming timeout indicator */}
      {isStreamingTimeout && !error && (
        <div
          className={`flex items-center gap-2 text-sm ${theme.colors.text.secondary} ${theme.colors.bg.secondary} px-3 py-2 rounded`}
          role="status"
          aria-live="polite"
        >
          <div className="w-4 h-4 flex-shrink-0 border-2 border-nb-accent border-t-transparent rounded-full animate-spin" />
          <span className="flex-1">Procesando... esto puede tomar más tiempo de lo habitual</span>
        </div>
      )}

      {/* Persisted last error (shown after auto-dismiss, cleared on success) */}
      {!error && lastDismissedError && (
        <div
          className={`flex items-center gap-2 text-xs ${theme.colors.text.secondary} opacity-60 px-3 py-1 rounded`}
          role="status"
          aria-live="polite"
        >
          <AlertCircle className="w-3 h-3 flex-shrink-0" />
          <span className="flex-1 truncate">Último error: {lastDismissedError}</span>
          {onRetry && (
            <button
              onClick={onRetry}
              className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] hover:opacity-100 transition-opacity"
              aria-label="Reintentar último mensaje"
            >
              <RefreshCw className="w-2.5 h-2.5" />
            </button>
          )}
          <button
            onClick={() => setLastDismissedError(null)}
            className="flex-shrink-0 hover:opacity-100 transition-opacity"
            aria-label="Descartar último error"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Mensaje de éxito — D1: CheckCircle icon instead of ✓ character */}
      {successMessage && (
        <div
          className={`flex items-center gap-2 text-sm ${theme.colors.text.success} ${theme.colors.bg.success} px-3 py-2 rounded`}
          role="status"
          aria-live="polite"
        >
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{successMessage}</span>
          {onDismissSuccess && (
            <button
              onClick={onDismissSuccess}
              className="flex-shrink-0 hover:opacity-70 transition-opacity"
              aria-label="Cerrar mensaje de éxito"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageStatusBar;
